import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_QUESTIONS = [
  "What is the main conclusion?",
  "Which methods were used?",
  "What are the strengths and weaknesses?",
];

const COLLECTION_QUESTIONS = [
  "What are the common themes across these documents?",
  "Compare the main findings across the collection.",
  "Which document should I read first, and why?",
];

export const AskAIPanel = ({ itemId, collectionId }) => {
  const isCollection = Boolean(collectionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const sessionIdRef = useRef(
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const bottomRef = useRef(null);

  const suggestions = isCollection ? COLLECTION_QUESTIONS : DOCUMENT_QUESTIONS;

  const send = async () => {
    const question = input.trim();
    if (!question || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const endpoint = isCollection
        ? `${API}/collections/${collectionId}/ask`
        : `${API}/items/${itemId}/ask`;
      const resp = await axios.post(
        endpoint,
        { question, session_id: sessionIdRef.current },
        { headers: { Authorization: `Bearer ${token}` }, timeout: isCollection ? 120000 : 60000 },
      );
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: resp.data.answer,
        assistantVersion: resp.data.assistant_version,
        responseLanguage: resp.data.response_language,
        analysisMode: resp.data.analysis_mode,
        sources: resp.data.cited_sources?.length ? resp.data.cited_sources : undefined,
      }]);
      if (resp.data.remaining !== null && resp.data.remaining !== undefined) {
        setRemaining(resp.data.remaining);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "AI assistant error. Please try again.";
      setMessages((prev) => [...prev, { role: "assistant", text: detail, isError: true }]);
      if (err.response?.status === 429) toast.error(detail);
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  return (
    <div className={isCollection ? "p-6 sm:p-8" : "p-8 border-b border-slate-200"} data-testid="ask-ai-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "Fraunces, serif" }}>
          <Sparkles className="w-5 h-5 text-orange-500" />
          {isCollection ? "Ask AI about this collection" : "Ask AI about this document"}
        </h2>
        {remaining !== null && (
          <span className="text-xs text-slate-400" data-testid="ask-ai-remaining">
            {remaining} question{remaining === 1 ? "" : "s"} left today
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mb-4">
        {isCollection
          ? "Ask one question across every document in this collection — the answer cites the documents it uses."
          : "Ask in any language — summarize, explain, or critically assess this document using its content."}
      </p>

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="ask-ai-suggestions">
          {suggestions.map((question) => (
            <button
              key={question}
              type="button"
              onClick={() => setInput(question)}
              className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs text-orange-800 hover:bg-orange-100"
            >
              {question}
            </button>
          ))}
        </div>
      )}

      {messages.length > 0 && (
        <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`ask-ai-message-${i}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-slate-900 text-white rounded-br-sm"
                    : m.isError
                      ? "bg-red-50 text-red-600 border border-red-100 rounded-bl-sm"
                      : "bg-slate-100 text-slate-800 rounded-bl-sm"
                }`}
              >
                <div>{m.text}</div>
                {m.sources?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-200 flex flex-wrap gap-1.5" data-testid={`ask-ai-sources-${i}`}>
                    {m.sources.map((s) => (
                      <Link
                        key={s.ref_id}
                        to={`/items/${s.item_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-orange-200 px-2 py-0.5 text-[11px] text-orange-800 hover:bg-orange-50"
                      >
                        <span className="font-semibold">{s.ref_id}</span>
                        <span className="max-w-[180px] truncate">{s.title}</span>
                      </Link>
                    ))}
                  </div>
                )}
                {m.role === "assistant" && !m.isError && (m.assistantVersion === "document-qa-v2" || m.assistantVersion === "collection-qa-v1") && (
                  <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] uppercase tracking-wide text-slate-400">
                    {m.assistantVersion === "collection-qa-v1"
                      ? `Collection answer${m.sources?.length ? ` · ${m.sources.length} document${m.sources.length === 1 ? "" : "s"} cited` : ""}`
                      : `Enhanced ${m.analysisMode ? "critical analysis" : "document answer"}`}
                    {m.responseLanguage ? ` · ${m.responseLanguage}` : ""}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 px-4 py-2.5 rounded-2xl rounded-bl-sm">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={isCollection ? "e.g. What do these documents say about leaching methods?" : "e.g. What are this document's main limitations?"}
          maxLength={1000}
          className="flex-1 h-10 px-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          data-testid="ask-ai-input"
        />
        <Button
          onClick={send}
          disabled={loading || !input.trim()}
          className="h-10 bg-slate-900 hover:bg-slate-700"
          data-testid="ask-ai-send-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default AskAIPanel;
